/*
 * Market locales. Each market has its own entry link (path prefix), so the
 * trafficked campaign links are unique per country; the /v2 variant appends
 * at the end as before, e.g. /ch-fr/v2. No prefix falls back to Germany,
 * which keeps the pre-locale links working unchanged.
 *
 *   /de     Germany              (German)
 *   /at     Austria              (German)
 *   /ch-de  Switzerland          (German)
 *   /ch-fr  Switzerland          (French)
 *   /ch-it  Switzerland          (Italian)
 *   /en     testing / fallback   (English)
 */

// The orange learn-more button is a picture asset, one per language, cut
// from the Figma frames (the Group 6227 nodes). They are @2x crops of the
// screen renders for now - re-cut at @4x via scripts/fetch-figma-buttons.sh
// when the Figma rate limit resets; the CSS border-radius on the button
// masks the background baked into the corners. EN is internal-only and
// keeps the pre-locale artwork. The share button is CSS + text
// (sharing-screen.css) and needs no artwork.
import learnMoreDe from './assets/buttons/learn-more-de.webp';
import learnMoreFr from './assets/buttons/learn-more-fr.webp';
import learnMoreIt from './assets/buttons/learn-more-it.webp';
import learnMoreImage from './assets/meh.webp';

// TODO(client): per-market campaign links not delivered yet - every market
// still points at the UK page the button linked to before localization.
const FALLBACK_SHOP_URL = 'https://www.miele.co.uk/cs/kitchen/cooking-and-baking/m-sense-cookware-7844';

const STRINGS = {
    de: {
        viewInAr: 'In AR Ansehen',
        loading: 'Laden...',
        learnMore: 'Mehr Erfahren',
        tapToStart: 'Zum Starten tippen',
        share: 'Teilen',
        learnMoreImg: learnMoreDe,
        // official lines from Figma frame 7355:2128
        headline1: 'Kein Anbrennen.',
        headline2: 'Kein Überkochen. Kein Problem.',
        subline: 'Die neuen Miele Kochfelder und M Sense Kochgeschirr.',
        pan: 'Pfanne',
        pot: 'Topf',
    },
    en: {
        viewInAr: 'View in AR',
        loading: 'Loading...',
        learnMore: 'Learn More',
        tapToStart: 'Tap To Start',
        share: 'Share',
        learnMoreImg: learnMoreImage,
        // internal fallback only - the Figma has no EN screens
        headline1: 'No Burn. No Overboil.',
        headline2: 'No Problem.',
        subline: 'The new Miele hobs and M Sense cookware.',
        pan: 'Frying pan',
        pot: 'Pot',
    },
    fr: {
        viewInAr: 'Afficher en RA',
        loading: 'Chargement…',
        learnMore: 'Découvrir',
        // The FR tap screen in the Figma (frame 7356:1104) really says
        // "En savoir plus", matching the delivered string list - intentional.
        tapToStart: 'En savoir plus',
        share: 'Partager',
        learnMoreImg: learnMoreFr,
        // official lines from Figma frame 7355:1941; the subline's break is
        // in the design (start-subline preserves it via white-space: pre-line)
        headline1: 'Rien ne brûle. Rien ne déborde.',
        headline2: 'Aucun problème.',
        subline: 'Les nouveaux plans de cuisson Miele\net la batterie de cuisine M Sense.',
        pan: 'Poêle',
        pot: 'Casserole',
    },
    it: {
        viewInAr: 'Visualizza in AR',
        loading: 'Caricamento...',
        learnMore: 'Scopri di più',
        tapToStart: 'Tocca per iniziare',
        share: 'Condividi',
        learnMoreImg: learnMoreIt,
        // official lines from Figma frame 7355:1903
        headline1: 'Niente brucia. Niente trabocca.',
        headline2: 'Niente problemi.',
        subline: 'Nuovi piani cottura Miele e stoviglie di cottura M Sense.',
        pan: 'Padella',
        pot: 'Pentola',
    },
};

export const LOCALES = {
    'de': { lang: 'de', market: 'Germany', shopUrl: FALLBACK_SHOP_URL },
    'at': { lang: 'de', market: 'Austria', shopUrl: FALLBACK_SHOP_URL },
    'ch-de': { lang: 'de', market: 'Switzerland (German)', shopUrl: FALLBACK_SHOP_URL },
    'ch-fr': { lang: 'fr', market: 'Switzerland (French)', shopUrl: FALLBACK_SHOP_URL },
    'ch-it': { lang: 'it', market: 'Switzerland (Italian)', shopUrl: FALLBACK_SHOP_URL },
    'en': { lang: 'en', market: 'English (fallback)', shopUrl: FALLBACK_SHOP_URL },
};

const DEFAULT_CODE = 'de';

function detectCode() {
    if (typeof window === 'undefined') return DEFAULT_CODE;
    const [first] = window.location.pathname.split('/').filter(Boolean);
    return LOCALES[first] ? first : DEFAULT_CODE;
}

const code = detectCode();

export const LOCALE = {
    code,
    ...LOCALES[code],
    strings: STRINGS[LOCALES[code].lang],
};
